import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

// Claims (billing). Mirrors the `formatClaim` shape from
// [server/src/routes/claims.js]. The server persists the snapshot-at-billing
// fields (payer, DOS, units/rate, mods, POS, auth#, dx) on the claim row;
// member-level PHI (address, member ID, subscriber) is pulled live from the
// client record at render time so we don't hold stale copies.

export type ClaimStatus = 'Ready' | 'Submitted' | 'Partial' | 'Paid' | 'Denied' | 'Voided';

// 'Payer' — insurance claim submitted to a payer via 837P (or manual).
// 'Client' — private-pay invoice billed directly to the client. Uses a
// separate per-practice sequence ('INV-###' vs 'CLM-###') and is excluded
// from 837P / secondary-COB paths.
export type BillTo = 'Payer' | 'Client';

// Snapshot of the payer's submission_method at claim creation. Null only on
// pre-migration historical rows. Drives the Billing UI's bulk-action split
// ("Create 837P" vs "Mark as Submitted") and the BillingDetailPage action.
export type ClaimSubmissionMethod = 'Manual' | 'Electronic' | null;

// Claim frequency code (837P CLM05-3). 'original' = new submission (1),
// 'corrected' = replacement of a prior claim (7), 'void' = cancellation
// of a prior claim (8). 7 and 8 require `originalClaimNumber`.
export type ClaimFrequency = 'original' | 'corrected' | 'void';

export interface ClaimCOBSnapshot {
    primaryPayer: string;
    primaryBilled: number;
    primaryAllowed: number;
    primaryPaid: number;
    primaryPR: number;
    primaryPayerRef: string;
    primaryPaymentDate: string | null;
    primaryAdjustmentAmt: number;
}

export interface Claim {
    id: string;
    claimNo: string;
    billTo: BillTo;
    sessionId: string | null;
    clientId: string | null;
    client: string;
    staff: string;
    serviceCode: string;
    payer: string;
    payerId: string;
    mod1: string;
    mod2: string;
    mod3: string;
    mod4: string;
    pos: string;
    serviceDate: string | null;
    units: number;
    unitRate: number;
    amount: number;
    paidAmount: number;
    prBalance: number;
    allowedAmount: number;
    status: ClaimStatus;
    denialReason: string;
    flags: string[];
    notes: string;
    primaryClaimId: string | null;
    cob: ClaimCOBSnapshot | null;
    payerRef: string;
    paymentDate: string | null;
    voidedAt: string | null;
    authNumber: string;
    referralNumber: string;
    frequency: ClaimFrequency;
    originalClaimNumber: string;
    dx1: string;
    dx2: string;
    created: string | null;
    submissionMethod: ClaimSubmissionMethod;
    submittedAt: string | null;
    submitConfirmation: string;
    serviceStartTime: string;
    serviceEndTime: string;
    dxPointers: DxPointers;
    prSourceClaimId: string | null;
    needsReview: boolean;
    needsReviewReason: string;
}

export type DxPointers = 'A' | 'B' | 'AB';

export interface ClaimStats {
    total: number;
    ready: number;
    submitted: number;
    partial: number;
    paid: number;
    denied: number;
    voided: number;
}

export interface ClaimWritePayload {
    sessionId?: string | null;
    clientId?: string;
    billTo?: BillTo;
    client?: string;
    staff?: string;
    serviceCode?: string;
    payer?: string;
    payerId?: string;
    mod1?: string;
    mod2?: string;
    mod3?: string;
    mod4?: string;
    pos?: string;
    serviceDate?: string;
    units?: number;
    unitRate?: number;
    amount?: number;
    paidAmount?: number;
    prBalance?: number;
    allowedAmount?: number;
    status?: ClaimStatus;
    denialReason?: string;
    flags?: string[];
    notes?: string;
    authNumber?: string;
    referralNumber?: string;
    frequency?: ClaimFrequency;
    originalClaimNumber?: string;
    dx1?: string;
    dx2?: string;
    payerRef?: string;
    paymentDate?: string | null;
    created?: string;
    submissionMethod?: ClaimSubmissionMethod;
    submittedAt?: string | null;
    submitConfirmation?: string;
    serviceStartTime?: string;
    serviceEndTime?: string;
    dxPointers?: DxPointers;
}

export interface MarkSubmittedPayload {
    ids: string[];
    submitDate: string;        // YYYY-MM-DD
    confirmation?: string;     // ≤ 64 chars; optional external reference
}

export interface MarkSubmittedResult {
    count: number;
}

export interface SecondaryClaimPayload {
    secondaryPayer: string;
    secondaryPayerId?: string;
    authNumber?: string;
}

interface ListOpts {
    dateFrom?: string;
    dateTo?: string;
    billTo?: BillTo;
    // When true, excludes claims that are already on a non-voided invoice;
    // when false, keeps only claims that ARE on a non-voided invoice. Off by
    // default. Used by the Invoices tab's "Unbilled" sub-tab.
    invoiced?: boolean;
    // Server-side exact status filter. Lets the billing list fetch only the
    // rows for the active tab so a practice with 100k Paid claims doesn't
    // pay to stream them all just to render the Ready tab.
    status?: ClaimStatus;
}

async function fetchClaims(opts: ListOpts = {}): Promise<Claim[]> {
    const params: Record<string, string> = {};
    if (opts.dateFrom) params.dateFrom = opts.dateFrom;
    if (opts.dateTo) params.dateTo = opts.dateTo;
    if (opts.billTo)  params.billTo   = opts.billTo;
    if (typeof opts.invoiced === 'boolean') params.invoiced = String(opts.invoiced);
    if (opts.status)  params.status   = opts.status;
    const res = await api.get<Claim[]>('/claims', { params });
    return res.data;
}

export function useClaims(opts: ListOpts = {}) {
    return useQuery({
        queryKey: [
            'claims',
            opts.dateFrom ?? 'any',
            opts.dateTo   ?? 'any',
            opts.billTo   ?? 'any',
            typeof opts.invoiced === 'boolean' ? String(opts.invoiced) : 'any',
            opts.status   ?? 'any',
        ] as const,
        queryFn: () => fetchClaims(opts),
        staleTime: 30_000,
    });
}

export function useClaim(id: string | undefined) {
    return useQuery({
        queryKey: ['claims', 'entry', id] as const,
        queryFn: async () => {
            const res = await api.get<Claim>(`/claims/${id}`);
            return res.data;
        },
        enabled: !!id,
    });
}

export function useClaimStats() {
    return useQuery({
        queryKey: ['claims', 'stats'] as const,
        queryFn: async () => {
            const res = await api.get<ClaimStats>('/claims/stats');
            return res.data;
        },
        staleTime: 30_000,
    });
}

export function useCreateClaim() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: ClaimWritePayload) => {
            const res = await api.post<Claim>('/claims', payload);
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['claims'] });
        },
    });
}

export function useUpdateClaim() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: ClaimWritePayload }) => {
            const res = await api.patch<Claim>(`/claims/${id}`, payload);
            return res.data;
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['claims'] });
            qc.invalidateQueries({ queryKey: ['claims', 'entry', vars.id] });
        },
    });
}

export function useDeleteClaim() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/claims/${id}`);
        },
        onSuccess: (_data, id) => {
            qc.removeQueries({ queryKey: ['claims', 'entry', id] });
            qc.invalidateQueries({ queryKey: ['claims'] });
        },
    });
}

// Bulk mark-submitted for Manual-payer Ready claims. Server enforces an
// all-or-nothing rule: the whole selection must be Payer + Manual + Ready.
// 409 responses come back with a `detail` pointing at the first offender.
export function useMarkSubmitted() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: MarkSubmittedPayload) => {
            const res = await api.post<MarkSubmittedResult>('/claims/mark-submitted', payload);
            return res.data;
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['claims'] });
            for (const id of vars.ids) qc.removeQueries({ queryKey: ['claims', 'entry', id] });
        },
    });
}

// Spawn a private-pay client claim from a payer claim's remaining PR balance.
// Server guardrails: parent must be Payer with prBalance > 0 and no active
// spawn. The spawn lands as a bill_to='Client' Ready claim tied back via
// prSourceClaimId, ready to be added to an invoice from the Unbilled tab.
export function useBillPR() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const res = await api.post<Claim>(`/claims/${id}/bill-pr`);
            return res.data;
        },
        onSuccess: (_data, id) => {
            qc.invalidateQueries({ queryKey: ['claims'] });
            qc.invalidateQueries({ queryKey: ['claims', 'entry', id] });
        },
    });
}

export function useCreateSecondaryClaim() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: SecondaryClaimPayload }) => {
            const res = await api.post<Claim>(`/claims/${id}/secondary`, payload);
            return res.data;
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['claims'] });
            qc.invalidateQueries({ queryKey: ['claims', 'entry', vars.id] });
        },
    });
}

// Derivation: validation flags a claim row carries based on its field
// completeness. Mirrors the legacy billing.js heuristic (missing payer,
// missing member ID, zero units, etc.) so the claim table can surface
// "needs attention" rows without the server needing to re-validate.
export function deriveClaimFlags(
    claim: Pick<Claim, 'payer' | 'payerId' | 'authNumber' | 'dx1' | 'units' | 'unitRate' | 'amount' | 'serviceCode'> & { billTo?: BillTo },
): string[] {
    const flags: string[] = [];
    const isInvoice = claim.billTo === 'Client';
    // Private-pay invoices don't submit to a payer, so payer ID / auth #
    // / diagnosis are irrelevant — checking them would flag every invoice.
    if (!isInvoice) {
        if (!claim.payer) flags.push('Missing payer');
        if (!claim.payerId) flags.push('Missing payer ID');
        if (!claim.authNumber) flags.push('Missing auth #');
        if (!claim.dx1) flags.push('Missing diagnosis');
    }
    if (!claim.serviceCode) flags.push('Missing service code');
    if (!claim.units || claim.units <= 0) flags.push('Zero units');
    if (!claim.unitRate || claim.unitRate <= 0) flags.push('Zero rate');
    if (!claim.amount || claim.amount <= 0) flags.push('Zero billed amount');
    return flags;
}
